import { FC, useState } from 'react'
import styled from 'styled-components'

import { sanitize } from '../../helpers/sanitize'

const Input = styled.input`
  padding: 10px 15px;
  border-radius: 5px;
  width: 300px;
  height: 40px;
  border: none;
`
const Button = styled.button`
  padding: 10px 15px;
  border-radius: 5px;
  text-decoration: none;
  background: white;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  width: fit-content;
  transition: 0.2s ease-in;
  border: none;
  cursor: pointer;
  margin: 0 auto;
  margin-top: 10px;
  font-weight: bold;

  &:hover {
    transition: 0.2s ease-in;
    opacity: 0.6;
  }
`
const Output = styled.div`
  margin-top: 50px;
`
const Error = styled.div`
  color: red;

  a {
    color: white;
  }
`
const Success = styled.div`
  color: green;
`

interface NewsletterFormProps {
  status: 'sending' | 'error' | 'success' | null
  message: string | Error | null
  onValidated: (formData: any) => void
}

const NewsletterForm: FC<NewsletterFormProps> = ({
  status,
  message,
  onValidated,
}) => {
  const [error, setError] = useState<null | string>(null)
  const [email, setEmail] = useState<string>('')

  /**
   * Handle form submit.
   *
   * @return {{value}|*|boolean|null}
   */
  const handleFormSubmit = () => {
    setError(null)

    if (!email) {
      setError('Please enter a valid email address')
      return null
    }

    const isFormValidated = onValidated({ EMAIL: email })

    // On success return true
    return email && email.indexOf('@') > -1 && isFormValidated
  }

  /**
   * Handle Input Key Event.
   *
   * @param event
   */
  const handleInputKeyEvent = (event: any) => {
    setError(null)
    // Number 13 is the "Enter" key on the keyboard
    if (event.keyCode === 13) {
      // Cancel the default action, if needed
      event.preventDefault()
      // Trigger the button element with a click
      handleFormSubmit()
    }
  }

  /**
   * Extract message from string.
   *
   * @param {String} message
   * @return {null|*}
   */
  const getMessage = (message: string) => {
    if (!message) {
      return null
    }
    const result = message?.split('-') ?? null
    if ('0' !== result?.[0]?.trim()) {
      return sanitize(message)
    }
    const formattedMessage = result?.[1]?.trim() ?? null
    return formattedMessage ? sanitize(formattedMessage) : null
  }

  return (
    <div>
      <div>
        <Input
          onChange={(event) => setEmail(event?.target?.value ?? '')}
          type="email"
          placeholder="Your email"
          className="appearance-none rounded-r rounded-l sm:rounded-l-none border border-gray-400 border-b block pl-4 pr-6 py-2 w-full bg-white text-sm placeholder-gray-400 text-gray-700 focus:bg-white focus:placeholder-gray-600 focus:text-gray-700 focus:outline-none"
          onKeyUp={(event) => handleInputKeyEvent(event)}
        />
        <Button onClick={handleFormSubmit}>Submit</Button>
      </div>
      <Output>
        {'sending' === status ? <p>Loading...</p> : null}
        {'error' === status || error ? (
          <Error
            dangerouslySetInnerHTML={{
              __html: error || getMessage(message as any),
            }}
          />
        ) : null}
        {'success' === status && !error && (
          <Success dangerouslySetInnerHTML={{ __html: sanitize(message) }} />
        )}
      </Output>
    </div>
  )
}

export default NewsletterForm
